import type { ManualContentBlock, ManualSection } from './user-manual-types';
import { getFigureImage } from './user-manual-figures';

const t = (text: string): ManualContentBlock => ({ type: 'text', text });
const tl = (text: string, indent: 1 | 2 | 3): ManualContentBlock => ({
  type: 'text',
  text,
  indent,
});
const fi = (n: number, caption: string): ManualContentBlock => ({
  type: 'figure',
  caption,
  image: getFigureImage(n),
});

/** Section 4 — Platform Capabilities (full manual text & figures). */
export const USER_MANUAL_SECTION_4: ManualSection[] = [
  {
    id: 'platform-capabilities',
    title: '4. Platform Capabilities (Datasets/Models/Use cases/Sandbox IDE)',
    level: 1,
    content: [
      'This section guides users on utilizing NHA\'s key features: Datasets, Models, Use Cases, and the Sandbox IDE. It covers the tools—search, sort, and filter—that simplify the process of locating relevant assets. Users will also learn the steps for managing assets, including uploading new ones and downloading existing ones. Furthermore, the guide explains how to import and interact with these assets within the Sandbox IDE, providing a unified environment for prototyping, testing, and deploying AI-driven solutions.',
    ],
    figures: [],
  },
  {
    id: 'datasets',
    title: '4.1. Datasets',
    level: 2,
    content: [
      'Datasets on NHA are comprehensive collections of data, encompassing both structured and unstructured formats. These collections are valuable resources, containing diverse information such as health records, satellite imagery, and public service logs. They are made available for analysis, research, and the development of AI solutions.',
    ],
    figures: [],
  },
  {
    id: 'datasets-listing',
    title: '4.1.1. Datasets Listing Page',
    level: 2,
    content: [],
    contentBlocks: [
      t(
        'The Datasets Listing page provides a comprehensive overview of all available Datasets. Users can quickly locate relevant assets using the search bar, sorting options, and a collapsible filter panel. This panel allows filtering by criteria such as concepts, organisation, organisation type, tags, file format, permissions, and last updated at. Each Dataset is displayed as a card, which includes the title, provider, readiness score, file type, access level, and a concise description. A "—>" button on the card gives users access to complete details or download options.',
      ),
      fi(37, 'Figure 37 — Dataset Listing Page'),
      t(
        '1. Datasets Search Bar: Positioned at the top of the Dataset Listing page, the search bar lets users enter keywords to quickly locate specific Datasets. As users type, it offers instant suggestions and filters the displayed cards to match titles, descriptions or tags, streamlining the discovery process.',
      ),
      fi(38, 'Figure 38 — Sample Search'),
      t('2. Sort feature: The sort feature lets users reorder the Dataset cards based on different criteria:'),
      tl('a. A–Z / Z–A: Alphabetical ordering by Dataset title.', 1),
      tl('b. Old to New/New to Old: Shows Datasets in ascending/descending order of upload date.', 1),
      fi(39, 'Figure 39 — Sort feature'),
      t(
        '3. Filter feature: The filter panel is divided into several sections, each allowing users to narrow the Dataset listing according to specific criteria:',
      ),
      tl('a. Organization Type:', 1),
      tl('i. All: Includes Datasets from every source.', 2),
      tl('ii. Public: Datasets published by government departments or publicly funded entities.', 2),
      tl('iii. Private: Datasets contributed by private companies or startups.', 2),
      tl('iv. Academic Institution: Datasets originating from universities and research institutes.', 2),
      tl('v. Other: Datasets from NGOs, community groups or any other type of organization.', 2),
      tl('b. Access Status:', 1),
      tl('i. Open: Publicly available Datasets with immediate download access.', 2),
      tl('ii. Restricted: Datasets requiring approval or elevated permissions to download.', 2),
      tl(
        'c. File Format: Choose from formats like CSV, PDF, TXT, Shapefile, image files etc. Selecting multiple types returns any Dataset offering at least one of the chosen formats.',
        1,
      ),
      tl(
        'd. Last Updated: Restrict results to Datasets updated in last 7 days, last 30 days, last 1 year or within a custom date range.',
        1,
      ),
      fi(40, 'Figure 40 — Filter options'),
      t('4. Dataset Card: Each Dataset card provides a snapshot of key information:'),
      tl('a. Access Status - “Open” or “Restricted” badge', 1),
      tl('b. Publishing Organization', 1),
      tl('c. Title', 1),
      tl('d. Short description', 1),
      tl('e. Tags', 1),
      tl('f. Data Readiness score', 1),
      tl('g. File format', 1),
      tl('h. Last updated date.', 1),
      tl('i. Action Button: “View Dataset” to open the full details and download options.', 1),
      fi(41, 'Figure 41 — Dataset Cards'),
    ],
    figures: [],
  },
  {
    id: 'dataset-details',
    title: '4.1.2. Dataset Details',
    level: 2,
    content: [],
    contentBlocks: [
      t(
        '1. Each dataset includes a comprehensive Metadata panel. This panel details the asset\'s origin, scope of usage, access regulations, and quality metrics. This critical information allows users to evaluate the dataset\'s relevance and technical compatibility before proceeding with download or integration into their projects. The following metadata fields are displayed:',
      ),
      tl('a. Title: Name of the dataset', 1),
      tl(
        'b. Long description: The long description section provides an in-depth narrative about the Dataset, offering context that goes beyond the basic metadata fields. It can outline the type of records (e.g., crop yield, soil composition, pesticide usage, irrigation history) and the data sources (such as individual farms, agricultural research stations, or commodity boards). It can also suggest potential use cases, ideal audience and indicate the type of possible analyses that can be performed using the Dataset.',
        1,
      ),
      tl(
        'c. Organisation: The government department, agency or entity that has the ownership of the published dataset.',
        1,
      ),
      tl(
        'd. Uploaded By: The user or team (or their organisation) that submitted the Dataset to NHA.',
        1,
      ),
      tl(
        'e. Geo Coverage: The geographic extent of the data (e.g., “State-level (Maharashtra)”).',
        1,
      ),
      tl(
        'f. Year Range: The temporal span of the records contained in the dataset (e.g., 2018–2024).',
        1,
      ),
      tl(
        'g. Dataset Permission: Access level—Open (no restrictions) or Restricted (requires approval)—that governs download and use.',
        1,
      ),
      tl('h. Last Updated: Date and time when the Dataset was most recently refreshed.', 1),
      tl('i. File Format: The downloadable format(s) provided (e.g., CSV, TXT, PDF).', 1),
      tl(
        'j. License: The legal terms under which the Dataset is made available (e.g., Maharashtra Open Health Data License v1.0).',
        1,
      ),
      tl(
        'k. Upload Frequency: How often the dataset is updated (e.g., Quarterly, Monthly, One-time).',
        1,
      ),
      tl(
        'l. Data Readiness: A data readiness score (percentage) that reflects completeness, documentation and cleanliness of the dataset.',
        1,
      ),
      tl(
        'm. Verified By: The role or individual—typically the Organisation Manager—who reviewed and approved the Dataset for publication.',
        1,
      ),
      fi(42, 'Figure 42 — Dataset Details'),
    ],
    figures: [],
  },
  {
    id: 'dataset-preview-panel',
    title: '4.1.3. Dataset Details & Preview',
    level: 2,
    content: [],
    contentBlocks: [
      t(
        'This page is split into two main areas - Version Files panel on the left and the Dataset Viewer on the right.',
      ),
      tl('a. Left Panel:', 1),
      tl(
        'i. Version Files: A collapsible list of all available file versions and associated file (e.g. annotation files of corresponding Dataset). Users can expand folders and click individual files to download.',
        2,
      ),
      tl('ii. File size: Size of the selected file in the Dataset is displayed here.', 2),
      tl(
        'iii. Last Updated: Timestamp of the upload date and time of the selected file is displayed.',
        2,
      ),
      tl('iv. License: The data license governing use.', 2),
      tl('b. Right Panel:', 1),
      tl(
        'i. Preview Table: An interactive snapshot of the dataset showing the first few rows and columns of the selected Dataset file.',
        2,
      ),
      tl(
        'ii. Column Descriptors Toggle: Enabling this option displays key statistics for each column—such as data type, record count, minimum, maximum, mean and standard deviation—so users can quickly assess the dataset’s structure and quality.',
        2,
      ),
      fi(43, 'Figure 43 — Dataset Preview'),
    ],
    figures: [],
  },
  {
    id: 'dataset-data-visualization',
    title: '4.1.4. Data Visualization',
    level: 2,
    content: [],
    contentBlocks: [
      t(
        'The Visualization panel lets users create basic charts directly from the Dataset—no coding required. By selecting one or more columns as the X-axis (dimensions) and additional columns as series (metrics), users can generate column, line or pie charts. Key components:',
      ),
      tl('a. Chart Type Selector: Choose between Column, Line or Pie charts.', 1),
      tl(
        'b. X-Axis / Series Picks: Click Add dimension to set the horizontal axis, add series to plot one or more metrics.',
        1,
      ),
      fi(44, 'Figure 44 — Dataset Visualization'),
    ],
    figures: [],
  },
  {
    id: 'dataset-upload-guide',
    title: '4.1.5. Dataset Upload',
    level: 2,
    content: [],
    contentBlocks: [
      t('Step 1: Click on ‘Start adding Dataset’ and set up your Dataset:'),
      tl('1. Enter Title & Permission:', 1),
      tl('c. Dataset Title: Give your dataset a clear, descriptive name.', 2),
      tl('d. Dataset Permission: Choose from following in the drop down:', 2),
      tl('i. Open: Viewable/downloadable by all registered users', 3),
      tl(
        'ii. Restricted: Users can view Dataset, but it is accessible only upon provider’s approval',
        3,
      ),
      tl(
        'iii. Private: Dataset is not viewable/downloadable by anyone, meant for personal use',
        3,
      ),
      tl('2. Choose Metadata Entry Method:', 1),
      tl(
        'a. Enter Metadata Manually: Click ‘Enter Metadata’ to fill in all fields via the form.',
        2,
      ),
      tl(
        'b. Import from JSON: Click ‘Upload JSON File’ to upload your metadata using an existing template file',
        2,
      ),
      fi(45, 'Figure 45 — Clicking on \'Start adding Dataset\''),
      fi(46, 'Figure 46 — Set up your Dataset'),
      t('Step 2: Enter metadata information:'),
      tl('1. Under metadata Information, fill out each mandatory field:', 1),
      tl(
        'a. Short Description: A one- or two-sentence summary of the Dataset’s content and purpose.',
        2,
      ),
      tl(
        'b. Tags: Add tags (e.g., “Community Health,” “Satellite Imagery”) to aid discovery.',
        2,
      ),
      tl(
        'c. Detailed Dataset Overview: This section is for providing a comprehensive description of the Dataset that expands beyond the basic metadata. It should detail the nature of the records contained (e.g., crop yield, soil composition, pesticide usage, irrigation history) and specify the sources of the data (such as individual farms, agricultural research stations, or commodity boards). Furthermore, this description can suggest potential use cases, identify the ideal audience for the data, and indicate the types of analyses that can be conducted using the Dataset.',
        2,
      ),
      tl(
        'd. Organisation: The government department, agency or entity that has the ownership of the published Dataset.',
        2,
      ),
      tl(
        'e. Year Range: The temporal span of the records contained in the Dataset (e.g., 2018–2024).',
        2,
      ),
      tl(
        'f. Dataset Type: Choose from catalogued types – structured/semi-structured/unstructured.',
        2,
      ),
      tl('g. Uploaded By: Autofilled using your existing details.', 2),
      tl(
        'h. Upload Frequency: How often the data will be updated (Daily, Weekly, Monthly, Annually, One-time).',
        2,
      ),
      tl('i. Geo Coverage: Geographic extent (e.g., State-level Maharashtra).', 2),
      tl('j. License: Legal terms governing reuse.', 2),
      tl('k. Organisation Type: Public, Private, Academic, or Other.', 2),
      tl(
        'l. File Format: Format of Dataset file which is being uploaded (CSV, TXT, PDF, GeoJSON, Shapefile, etc.).',
        2,
      ),
      tl(
        '2. Link Related Assets: Add Associated Datasets or Add Associated Models to create pre-defined connections between your new Dataset and other platform content.',
        1,
      ),
      fi(47, 'Figure 47 — Adding metadata information'),
      t(
        'Step 3: Upload Dataset - The user should upload the relevant files according to the file format selected in Step 2. Users can upload multiple files using the "Upload More" button. Additionally, users can clear or delete any previously uploaded files and upload new files or an entirely new set using the "Clear All" button.',
      ),
      fi(48, 'Figure 48 — Uploading data'),
      t(
        'Step 4: Review and Publish - After your files finish uploading, you’ll be taken to a summary page displaying all metadata fields and tags you entered, list of uploaded files and their preview (if uploaded data is structured). Carefully verify each detail—move back to the respective section to correct any information. When everything looks correct, click Publish.',
      ),
      t(
        'Step 5: Approval - Approval requests are sent to the Organisation Manager for review. Once they approve, the Dataset gets published on the NHA Dataset listing page.',
      ),
    ],
    figures: [],
  },
  {
    id: 'dataset-download-guide',
    title: '4.1.6. Dataset Download',
    level: 2,
    content: [],
    contentBlocks: [
      t('Depending on a Dataset’s permission level, the steps to download differ slightly:'),
      tl(
        '1. Open Datasets: Click the ‘Download’ on the Dataset details page. The entire Dataset (all files and folders) is packaged into a single ZIP archive and begins downloading immediately.',
        1,
      ),
      fi(49, 'Figure 49 — Downloading Open Datasets'),
      t('2. Restricted Datasets:'),
      tl('a. Click the ‘Request Access’ button on the Dataset details page.', 1),
      tl('b. A request form pops up; add a brief justification for why you need access.', 1),
      tl('c. Submit the request—NHA notifies the Dataset’s provider.', 1),
      tl(
        'd. Once approved, you’ll receive an email notification, and a ‘Download button’ appears in the details page.',
        1,
      ),
      tl('e. Click ‘Download’ button to retrieve the full Dataset.', 1),
      fi(50, 'Figure 50 — Downloading Restricted Datasets'),
    ],
    figures: [],
  },
  {
    id: 'dataset-sandbox-ide-usage',
    title: '4.1.7. Using Dataset in Sandbox IDE',
    level: 2,
    content: [],
    contentBlocks: [
      t(
        'Once the sandbox IDE has been instantiated, you will see a templated starter notebook with the following information -',
      ),
      tl('1. The type of notebook instance - CPU only or GPU instance', 1),
      tl(
        '2. The available resources for that notebook - Available CPU cycles and Available RAM, Available VRAM (in case of GPU notebook)',
        1,
      ),
      t(
        'The started NHA SDK requires you to input your credentials and the dataset of interest and fetches the Dataset of interest into your workspace.',
      ),
      t(
        'You can fetch a Dataset ID from the detail page of that particular Dataset by clicking on copy icon.',
      ),
      fi(51, 'Figure 51 — Copying Dataset ID'),
      t('Figure below shows the section of the starter sdk which allows you to download a dataset.'),
      fi(52, 'Figure 52 — Downloading Dataset in IDE'),
      t(
        'Once the fetching process is complete, you will find the dataset of interest in the “In-Notebook” file interface as shown in the figure below.',
      ),
      fi(53, 'Figure 53 — Dataset downloaded in in-notebook interface'),
    ],
    figures: [],
  },
  {
    id: 'models-capability',
    title: '4.2. Models',
    level: 2,
    content: [
      'NHA offers ready-to-use, pre-trained machine learning models. These models are available for download or direct testing on the platform, allowing users to address specific challenges such as text classification, image recognition, or making predictions.',
    ],
    figures: [],
  },
  {
    id: 'models-listing',
    title: '4.2.1. Models Listing Page',
    level: 2,
    content: [],
    contentBlocks: [
      t(
        'The Models Listing page serves as the main entry point for discovering, evaluating, and utilizing pre-trained Models. Users can efficiently find the desired model by using the search bar, sorting, and filtering options. Each model\'s card provides essential details, allowing users to review key information before proceeding to model-specific documentation or testing environments.',
      ),
      fi(54, 'Figure 54 — Models Listing Page'),
      t(
        '1. Models Search: Positioned at the top of the page, the Models search bar lets users enter keywords (e.g., “translation,” “disease detection”) to instantly narrow the Models list.',
      ),
      fi(55, 'Figure 55 — Models Search Bar'),
      t(
        '2. Sort feature: To the right of the search bar is a sort menu that reorders the model cards based on:',
      ),
      tl('a. A–Z / Z–A: Alphabetical by model name', 1),
      tl('b. Old to New/New to Old: Shows Models in ascending/descending order of upload date.', 1),
      t('Selecting an option immediately refreshes the listing in the chosen order.'),
      fi(56, 'Figure 56 — Model Sort feature'),
      t('3. Filter feature: The Model filter panel enables users to narrow the model listing by multiple criteria:'),
      tl('a. Organisation Type:', 1),
      tl('i. All: Includes Models from every source.', 2),
      tl('ii. Public: Models published by government departments or publicly funded entities.', 2),
      tl('iii. Private: Models contributed by private companies or startups.', 2),
      tl('iv. Academic Institution: Models originating from universities and research institutes.', 2),
      tl('v. Other: Models from NGOs, community groups or any other type of organization.', 2),
      tl(
        'b. Industry: Applying an industry filter limits results to Models tagged under those sectors. Type to search or select one or more industries—such as Healthcare, Financial Services, Agriculture, Manufacturing, Energy, Education and more.',
        1,
      ),
      tl(
        'c. Model Type: Model categories such as Text Classification, Image Classification, Object Detection, Named Entity Recognition, Speech-to-Text, Text Summarization, Sentiment Analysis, Time Series Forecasting, Clustering, Anomaly Detection, Recommendation, Language Translation, Question Answering, Text Generation, Semantic Search, and many more.',
        1,
      ),
      tl('d. File Format: This filter helps in listing Models with specific file formats', 1),
      tl('i. Traditional ML: PKL, JOBLIB, SAV, RDS', 2),
      tl('ii. Deep Learning (TensorFlow / Keras): HDF5, PB, TFLITE, KERAS', 2),
      tl('iii. Deep Learning (PyTorch): PT, PTH, CKPT', 2),
      tl('iv. Cross-Framework / Optimized: ONNX, SAFETENSORS, TRT', 2),
      tl('v. Enterprise/Interchange: MLMODEL, TFLITE, PMML, JSON, BIN', 2),
      tl('vi. Other', 2),
      tl('e. Permission:', 1),
      tl('i. Open: Publicly available Models with immediate download access.', 2),
      tl('ii. Restricted: Models requiring approval or elevated permissions to download.', 2),
      tl(
        'f. Last Updated: Restrict results to Models updated in last 7 days, last 30 days, last 1 year or within a custom date range.',
        1,
      ),
      fi(57, 'Figure 57 — Models Filter options'),
      t('4. Model Card: Each Model card provides a snapshot of following key information:'),
      tl('a. Access Status - “Open” or “Restricted” badge', 1),
      tl('b. Publishing Organization', 1),
      tl('c. Title', 1),
      tl('d. Short description', 1),
      tl('e. Tags', 1),
      tl('f. File format', 1),
      tl('g. Last updated date.', 1),
      tl('h. Action Button: “View Model” to open the full details and download options.', 1),
      fi(58, 'Figure 58 — Model Cards'),
    ],
    figures: [],
  },
  {
    id: 'model-details-page',
    title: '4.2.2. Model Details',
    level: 2,
    content: [],
    contentBlocks: [
      t(
        'When users select an Model card, they arrive at the Model Detail page, which is organized into the following sections:',
      ),
      t('1. Page Header & Actions:'),
      tl(
        'a. Breadcrumb Trail: Shows “Home / Models / [Model Name]” so users know their location.',
        1,
      ),
      tl(
        'b. Model Title & Permission: The model’s name (e.g., “Telugu Grievance Classification Model”) is accompanied by an Open or Restricted badge.',
        1,
      ),
      tl(
        'c. Download Button: For Open models, users can immediately download the model archive; for Restricted models, this button becomes active once access is granted.',
        1,
      ),
      t('2. Description: A concise summary describing the model’s purpose, key capabilities.'),
      t(
        '3. Tags: Keywords (e.g., “Grievance,” “Classification”) that show the model’s domains and functions.',
      ),
      t('4. Model Details Panel: A structured metadata table presents:'),
      tl(
        'a. Organisation: The government department, agency or entity that has the ownership of the published Model.',
        1,
      ),
      tl(
        'b. Industry: The sector classification—such as Public Services, Healthcare or Agriculture—that indicates the model’s primary domain, if any. If model is not associated to any industry, it is depicted as sector agnostic',
        1,
      ),
      tl(
        'c. Uploaded By: The individual or team (and their organisation) that submitted the model to NHA.',
        1,
      ),
      tl('d. Model Permission: Access level (Open or Restricted)', 1),
      tl('e. Last Updated: Timestamp of the most recent update of the model', 1),
      tl('f. License: Governing usage terms (e.g., Apache 2.0)', 1),
      tl('g. Model Type: Task category (e.g., Text Classification)', 1),
      tl('h. File Type: Format of the model bundle (e.g., Safetensors)', 1),
      tl('i. File Size: Download size of complete model repository.', 1),
      fi(59, 'Figure 59 — Models detail page'),
    ],
    figures: [],
  },
  {
    id: 'model-upload-guide',
    title: '4.2.3. Model Upload',
    level: 2,
    content: [],
    contentBlocks: [
      t('Step 1: Click on ‘Start adding Model’ and then, set up your Model.'),
      tl(
        '1. Model Title: Enter a concise, descriptive name that clearly reflects the model’s function (e.g., “Telugu Grievance Classification Model”).',
        1,
      ),
      t('2. Model Permission:'),
      tl(
        'a. Open: The model is publicly accessible—any user can download or invoke it immediately without approval.',
        1,
      ),
      tl(
        'b. Restricted: Access requires an approval from the provider of that particular Model.',
        1,
      ),
      tl('c. Private: The model is visible and usable only by the provider.', 1),
      t('3. Metadata Entry Options:'),
      tl(
        'a. Enter Metadata Manually: Opens a form where you have to fill short/long descriptions, tags and structured fields from scratch.',
        1,
      ),
      tl(
        'b. Import from JSON: Upload an existing NHA-compatible JSON manifest to pre-populate all metadata fields—ideal for automated workflows or bulk model registrations.',
        1,
      ),
      fi(60, 'Figure 60 — Clicking on \'Start adding Model\''),
      fi(61, 'Figure 61 — Set up your Model'),
      t(
        'Step 2: Provide Model Information - Once the basic setup is complete, fill in the model’s descriptive and structured metadata:',
      ),
      tl(
        '1. Short Description: A one- or two-sentence summary of the model’s task and capabilities.',
        1,
      ),
      tl(
        '2. Tags: Add up to five keywords that aid discovery (e.g., “Text Classification,” “NLP,” “Grievance”).',
        1,
      ),
      tl(
        '3. Long Description: In the long description, users should clearly explain what the model is meant to do, what kind of input it takes and what output it gives. They should also mention key features like support for different languages, the ability to customize outputs. Finally, it’s helpful to describe how the model can be used or where it can be applied.',
        1,
      ),
      t('4. Model Details: Fill in all mentioned details:'),
      tl(
        'a. Organisation: The government department, agency or entity that has the ownership of the published Dataset.',
        2,
      ),
      tl('b. Organisation Type: Public, Private, Academic, NGO/Non-profit or Other.', 2),
      tl('c. Uploaded By: Please mention your user name, team name or organization', 2),
      tl('d. License: Legal terms governing model reuse (e.g., Apache 2.0).', 2),
      tl('e. File Size: Approximate download size (e.g., 100 MB).', 2),
      tl('f. Model Type: AI task category (Text Classification, Object Detection, etc.).', 2),
      tl('g. File Format: Bundle format(s) supported (SAFETENSORS, ONNX, PKL, etc.).', 2),
      tl(
        'h. Industry: Select sector classification—such as Healthcare or Agriculture—that indicates the model’s primary domain, if any. If model is not associated to any industry, please select sector agnostic.',
        2,
      ),
      tl('i. Media Url: Url link for the the Model (eg., github link)', 2),
      fi(62, 'Figure 62 — Enter Model information'),
      t(
        'Step 3: Upload Your Model: If the user provides a Media URL, the Model will be created automatically in Step 2. However, if no Media URL is entered, the user will be directed to Step 3 to proceed with manual file upload. In this step, users must upload the relevant files according to the file format selected in Step 2. They have the option to upload multiple files using the "Upload More" button. Additionally, users can clear or delete any previously uploaded files and upload new files or an entirely new set using the "Clear All" button.',
      ),
      fi(63, 'Figure 63 — Uploading Model'),
      t(
        'Step 4: Review and Publish: Before publishing, review all details including the short and long descriptions, tags, metadata fields, and uploaded files to ensure accuracy. Confirm that the correct permission level is selected. Once everything is verified, click Publish to proceed. Open Models go live immediately and are discoverable by all users.',
      ),
      t(
        'Step 5: Approval: Approval requests are sent to the Organisation Manager for review. Once they approve, the Model gets published on the Model listing page.',
      ),
    ],
    figures: [],
  },
  {
    id: 'model-download-guide',
    title: '4.2.4. Model Download',
    level: 2,
    content: [],
    contentBlocks: [
      tl(
        '1. Open: For ‘Open’ Models, users can simply click the Download button on the model detail page. The model file is immediately packaged and begins downloading without any additional steps or approvals.',
        1,
      ),
      tl(
        '2. Restricted: For ‘Restricted’ Models, users must first request access by clicking the Request Access button on the detail page. A form will appear prompting the user to provide a brief justification. Once submitted, the request is sent to the model\'s provider or organisation manager for approval. After approval, the user receives an email notification and the Download button becomes active on the detail page, allowing them to retrieve the model file.',
        1,
      ),
      fi(64, 'Figure 64 — Model Download'),
    ],
    figures: [],
  },
  {
    id: 'sandbox-ide-overview',
    title: '4.3. Sandbox IDE',
    level: 2,
    content: [],
    contentBlocks: [
      t(
        'The Sandbox IDE is a cloud development environment within NHA that enables users—especially data scientists, researchers and developers—to build, test and deploy Models using NHA Datasets and AI resources. It offers secure, compute-enabled notebooks along with access to curated toolsets and learning materials.',
      ),
      fi(75, 'Figure 75 — Sandbox IDE section'),
    ],
    figures: [],
  },
  {
    id: 'sandbox-creating-notebooks',
    title: '4.3.1. Creating Notebooks',
    level: 2,
    content: [],
    contentBlocks: [
      t('To start working in the IDE, users must first create a new notebook.'),
      t('Step 1: Click on “Create New Notebook” from the Sandbox IDE home page.'),
      fi(76, 'Figure 76 — Clicking on Create New Notebook'),
      t('Step 2: Click on Create IDE button.'),
      fi(77, 'Figure 77 — Clicking on Create IDE button'),
      t('Step 3: A pop-up window will prompt the user to:'),
      tl(
        '1. Enter a notebook name. The notebook name should have all small letters with no special characters or spaces in it.',
        1,
      ),
      tl('2. Choose an instance type (e.g., CPU or GPU, if available)', 1),
      tl(
        '3. Click Submit to launch the notebook. Once submitted, the user is taken to an IDE interface where they can write and run code.',
        1,
      ),
      fi(78, 'Figure 78 — Entering Notebook information'),
      t('Step 4: Your IDE notebook will open.'),
      t('Note: You might need to wait for 1-2 minutes for your notebook to get opened'),
      fi(79, 'Figure 79 — IDE Notebook'),
    ],
    figures: [],
  },
  {
    id: 'sandbox-gpu-compute',
    title: '4.3.2. Requesting GPU-based Compute Resources',
    level: 2,
    content: [],
    contentBlocks: [
      t(
        'This section allows users to apply for GPU-based compute access to support Model training and development within the Sandbox IDE. Since GPU resources are limited and allocated based on merit, users are encouraged to be as clear, descriptive, and logical as possible while filling out the request form. Applications are evaluated based on project impact, technical need, and alignment of objective with Maharashtra’s AI mission.',
      ),
      t('Step 1: Go to ‘My Projects’ section of your profile.'),
      t('Step 2: Click on ‘Request GPU Compute’ button.'),
      t('Step 3: Fill in Applicant Information'),
      tl('1. Your name is auto-filled.', 1),
      tl(
        '2. Answer the question: Are you a member of an organisation registered on NHA?',
        1,
      ),
      tl('a. If Yes:', 1),
      tl(
        'i. Enter your Organisation Name, Organisation Type, and Organisation Location (City, State).',
        2,
      ),
      tl(
        'ii. Provide Organisation Website, your Job Title, and official email ID (business email).',
        2,
      ),
      tl('b. If No:', 1),
      tl('i. Provide your personal email ID and location (City, State).', 2),
      tl('ii. Optionally, add your LinkedIn profile and mobile number.', 2),
      t('Step 4: Provide Project Overview - Fill out the following required fields:'),
      tl('1. Project Title', 1),
      tl('2. Brief Description of your Model or use case (max 250 words).', 1),
      tl('3. Select the relevant sector (e.g., Healthcare, Education, Agriculture, etc.).', 1),
      tl(
        '4. Explain the problem you are solving and what makes your solution innovative.',
        1,
      ),
      tl('5. Mention the NHA Dataset you are using.', 1),
      tl(
        '6. Identify your end users or beneficiaries (e.g., Government, Startups, Citizens).',
        1,
      ),
      tl('7. Select the current stage of your project (PoC, MVP, Scaling, etc.).', 1),
      t(
        'Step 5: Enter Technical Information for GPU Needs - This section helps NHA assess the required resources:',
      ),
      tl('1. Specify the type of AI/ML model (e.g., Transformer, LLM, CNN).', 1),
      tl('2. Indicate if the model is pre-trained or newly trained.', 1),
      tl('3. Select the input data type (e.g., image, text, tabular, multimodal).', 1),
      t('4. Mention the approximate data volume and add:'),
      tl('a. Number of training iterations/epochs', 2),
      tl('b. Estimated batch size', 2),
      tl('5. Specify the estimated GPU hours needed.', 1),
      tl('6. Write a brief justification explaining why GPU access is required.', 1),
      tl('7. Optionally, share your preferred time slots for using compute.', 1),
      tl(
        '8. Confirm whether you\'ve used NHA compute before. If yes, provide details of your previous usage.',
        1,
      ),
      t(
        'Step 6: Share Team Credentials - This section is optional but strengthens your request:',
      ),
      tl('1. Indicate your team size.', 1),
      tl('2. List key team members and roles.', 1),
      tl('3. Summarise any past AI/ML experience.', 1),
      tl('4. Upload relevant projects, publications, or recognitions.', 1),
      tl('5. Mention if you have any government, incubator, or VC support.', 1),
      tl('6. Optionally disclose your organisation’s valuation.', 1),
      t('Step 7: Final Declaration & Submission'),
      tl('1. Tick the checkbox to confirm the accuracy of your information.', 1),
      tl(
        '2. Acknowledge that GPU allocation is based on evaluation criteria and availability.',
        1,
      ),
      tl('3. Click “Submit Request” to complete the process.', 1),
    ],
    figures: [],
  },
  {
    id: 'sandbox-using-compute',
    title: '4.3.3. Using Compute in IDE',
    level: 2,
    content: [
      'Once compute access is approved, users can launch or switch their notebooks to run on the allocated GPU resources. The notebook automatically connects to the compute backend, allowing users to perform resource-intensive tasks such as model training, evaluation, inference, or fine-tuning directly within the environment.',
      'Approved users are allotted a compute quota (such as GPU hours or sessions), which can be tracked through their dashboard. Each quota comes with an expiration timeline and may be subject to limits based on prior usage and overall platform capacity. Users are expected to use their allocated compute efficiently and are encouraged to request only what they truly need to ensure fair access for all members of the NHA community.',
      'Compute usage will be tracked and subtracted from the available quota. Once the available quota for the user is breached, there will be a warning that will flash and all your current work will be saved and notebook will be suspended.',
    ],
    figures: [],
  },
  {
    id: 'sandbox-toolsets',
    title: '4.4. Leveraging Built-in Toolsets',
    level: 2,
    content: [],
    contentBlocks: [
      t(
        'The Sandbox IDE on NHA comes pre-loaded with a wide range of open-source toolsets to support the full AI/ML workflow—from data analysis and feature engineering to model training, deployment, and monitoring. Popular libraries such as MLflow, DVC, TensorBoard, Streamlit, Plotly, and BentoML are available, enabling users to build, track, visualize, and serve models efficiently without additional setup.',
      ),
      fi(80, 'Figure 80 — Toolsets section'),
    ],
    figures: [],
  },
  {
    id: 'sandbox-learning-library',
    title: '4.5. Learning using Library',
    level: 2,
    content: [],
    contentBlocks: [
      t('Users can explore the Learning Library, which includes:'),
      tl(
        '1. Articles and tutorials on ML techniques, evaluation metrics, and model validation',
        1,
      ),
      tl(
        '2. Step-by-step notebooks and guides for specific tasks like NLP and computer vision',
        1,
      ),
      tl('3. Video walkthroughs demonstrating real-world AI applications', 1),
      fi(81, 'Figure 81 — Library Section'),
      t(
        'The Sandbox IDE combines ready-to-use compute, libraries, and learning materials to streamline the end-to-end AI development experience on NHA.',
      ),
    ],
    figures: [],
  },
];
